import s from './MainChatsView.module.css'
import { useInput } from '@/hooks'
import Search from '@UI/Search'
import Button, { CollapseBtn } from '@UI/Button'
import { useEffect, useRef, useState } from 'react'
import { AddNewChatIcon, CloseIcon, CompressIcon } from '@/utils/icons'
import { chatApi } from '@/api'
import ChatViewList from '@common/Chat/components/ChatViewList'
import { useAsyncRetry, useClickAway } from 'react-use'
import UserList from '@common/Chat/components/UserList'
import { useSelector } from 'react-redux'
import { selectProfile } from '@store/profile.reducer'
import { SettingsService } from '@/services/SettingsService'
import { SETTINGS_TYPES } from '@/constant'
import rolesEnum from '@/constant/rolesEnum'
import useIsTechSupport from '../SupportChatButton/useIsTechSupport'
import { useChatContext } from '../ChatContext'
import cn from 'classnames'

const MainChatPage = ({ setPage, activeUsers, unReadChats, params, getActiveUsers }) => {
  const userId = useSelector(selectProfile)?.id
  const { isTechSupport, chatIsDisabledButTechnicalSupportIsEnabled } = useIsTechSupport()

  const [bindSearch, valueSearch, reset] = useInput('')
  const [users, setUsers] = useState([])
  const [isShowUsers, setIsShowUsers] = useState(false)
  const userRef = useRef(null)
  const [state, setState] = useState({
    search: null,
  })
  const initialTab = (chatIsDisabledButTechnicalSupportIsEnabled || params?.selectedTab === 'support') ? 'support' : 'all';

  const [selectedTab, setSelectedTab] = useState(initialTab) // 'all', 'direct', 'group', 'support'
  const profile = useSelector(selectProfile)

  const chatContext = useChatContext()

  useEffect(() => {
    if (selectedTab === 'support' && !isTechSupport) {
        setSelectedTab('all')
    }
    if(chatIsDisabledButTechnicalSupportIsEnabled && selectedTab !== 'support' && isTechSupport) {
        setSelectedTab('support')
    }
  }, [selectedTab, isTechSupport, chatIsDisabledButTechnicalSupportIsEnabled])

  const [initialLoad, setInitialLoad] = useState(true)

  useEffect(() => {
    const loadInitialData = async () => {
      await getActiveUsers()
      setInitialLoad(false)
    }
    loadInitialData()
  }, [])

  const [chats, setChats] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  // Функция для загрузки чатов с пагинацией
  const loadChats = async ({page = 1, isLoadMore = false}) => {
    if (initialLoad || isLoadingMore) return
    
    try {
      if (isLoadMore) {
        setIsLoadingMore(true)
      }
      
      const response = await chatApi.getChatList({ 
        'per-page': 20, 
        page: page,
        type: selectedTab || 'all' 
      })
      
      if (response?.data?.data) {
        if (isLoadMore) {
          setChats(prev => [...prev, ...response.data?.data])
        } else {
          setChats(response.data?.data)
          setCurrentPage(1)
        }
        
        setHasMore(response?.data?._meta?.pageCount !== page)
        
        if (isLoadMore) {
          setCurrentPage(page)
        }
      }
    }
    finally {
      if (isLoadMore) {
        setIsLoadingMore(false)
      }
    }
  }

  useEffect(() => {
    loadChats({page: 1})
  }, [selectedTab, initialLoad])

  const handleLoadMore = () => {
    if (!isLoadingMore && hasMore) {
      loadChats({page: currentPage + 1, isLoadMore: true})
    }
  }

  const getChatListByIds = async () => {
    const response = await chatApi.getChatListByIds({ 
        chatIds: unReadChats?.map(el => el?.chatId) 
    })
    if(response?.data?.data){
      setChats(
        (prev) => prev?.map((el) => response?.data?.data?.find(elem => elem?.id == el.id) ?? el)
      )
    }
  } 
  useEffect(()=>{
      getChatListByIds();
  }, [unReadChats])

  const canStartChat = () => {
    const childrenCanStartChat =
      SettingsService.get(SETTINGS_TYPES.GENERAL)?.isChildrenCanStartChat ===
      '1'
    const parentCanStartChat =
      SettingsService.get(SETTINGS_TYPES.GENERAL)?.isParentCanStartChat === '1'

    if (profile.role === rolesEnum.children) return childrenCanStartChat
    if (profile.role === rolesEnum.parent) return parentCanStartChat

    return true
  }

  useClickAway(userRef, () => {
    setIsShowUsers(false)
  })

  const getUsers = () => {
    chatApi
      .getUsersChatsList({
        search: valueSearch,
      })
      .then((response) => {
        if (response?.data) {
          setUsers(response?.data.slice(0, 20))
        }
      })
  }

  useEffect(() => {
    getUsers()
  }, [state])

  const dataSearch = {
    bind: bindSearch,
    onChange: () => {
      setState({
        search: valueSearch,
      })
    },
  }

  const handleCloseUsersList = () => {
    setIsShowUsers(false)
    reset()
  }

  const handleCollapseBtnClick = () => setPage('default')

  return (
    <div className={s.page}>
      <div className={cn(s.tabs, 'custom-scroll')}>
        {
          !chatIsDisabledButTechnicalSupportIsEnabled && (
            <>
              <Button
                action={() => setSelectedTab('all')}
                className={cn(s.tabButton, selectedTab === 'all' && s.tabActive)}
                newDesign
              >
                Все
              </Button>
              <Button
                action={() => setSelectedTab('direct')}
                className={cn(s.tabButton, selectedTab === 'direct' && s.tabActive)}
                newDesign
              >
                Личные
              </Button>
              <Button
                action={() => setSelectedTab('group')}
                className={cn(s.tabButton, selectedTab === 'group' && s.tabActive)}
                newDesign
              >
                Группы
              </Button>
            </>
          )
        }
       
        <Button
          action={async () => {
            // если учасник адмитнов тех. поддержки
            if (isTechSupport) {
              setSelectedTab('support')
              return
            }

            const response = await chatApi.createSupportChat({
              user_id: userId,
            })

            if (response.data?.data?.chat_id) {
              chatContext.changePage('inChat', response.data?.data?.chat_id)
            }
          }}
          className={cn(s.tabButton, selectedTab === 'support' && s.tabActive)}
          newDesign
        >
          Тех. поддержка
        </Button>
      </div>
      <div className={s.header}>
        {canStartChat() && (
          <>
            <Search
              placeholder="Введите ФИО собеседника"
              className={s.search}
              {...dataSearch}
              withClose={false}
              onFocus={() => setIsShowUsers(true)}
            />
            {isShowUsers && (
              <button className={s.close} onClick={handleCloseUsersList}>
                <CloseIcon />
              </button>
            )}
            <button className={s.addBtn} onClick={() => setPage('create')}>
              <AddNewChatIcon width={'36'} height={'36'} />
            </button>
            <span className={s.desc}>Создать чат</span>
          </>
        )}
        <CollapseBtn
          onClick={handleCollapseBtnClick}
          color="red"
          style={{ marginLeft: canStartChat() ? '4px' : 'auto' }}
        />
      </div>
      {isShowUsers && (
        <UserList users={users} setPage={setPage} activeUsers={activeUsers} />
      )}
      {!isShowUsers && (
        <>
          <ChatViewList
            chats={chats}
            setPage={setPage}
            setIsNeedUpdate={() => loadChats({page: 1})}
            unReadChats={unReadChats}
          />
         
          {hasMore && (
            <div className={s.loadMoreContainer}>
              <button
                onClick={handleLoadMore}
                className={s.loadMoreButton}
                disabled={isLoadingMore}
                newDesign
              >
                {isLoadingMore ? 'Загрузка...' : 'Подгрузить ещё'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default MainChatPage